import { Box, IconButton, Typography, useTheme } from '@mui/material';
import IconEdit from '@assets/icons/edit.svg?react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InfoList from './InfoList';
import {
 AccessGroupsDTO,
 GroupDTO,
 UsersDTO,
} from '@redux/identity/identityTypes';
import ChangePassword from '@/components/adminUsers/ChangePassword';
import GenerateAPIKey from '@/components/adminUsers/GenerateAPIKey';
import ResetUserPassword from '@/components/adminUsers/ResetUserPassword';
import XTabs from '../XTabs';
import {
 usersGetUserGroupsStart,
 usersGetSelfAccessGroupsStart,
 updateItemStart,
} from '@redux/users/usersSlice';
import { RootState } from '@redux/store';
import { MutableRefObject } from 'react';
import { CustomGridApi } from '../XTable/types';
import { UserAccessGroupsSection } from './UserAccessGroupsSection';
import { UserSettingsSwitches } from './UserIdentitySections';
import { useTranslation } from 'react-i18next';
import accessGroupsSlice from '@redux/accessGroups/accessGroupsSlice';
import DatabaseCheck from './DatabaseCheck';
import DatabaseList from './DatabaseList';
const fields = [
 'Name',
 'Surname',
 'Email',
 'Phone',
 'AuthEmail',
 'AuthPhone',
 'RequestToChangePasswordAfterDays',
];

const UsersSidebar = ({
 item,
 apiRef,
}: {
 item: UsersDTO;
 apiRef: MutableRefObject<CustomGridApi>;
}) => {
 const dispatch = useDispatch();
 const th = useTheme();
 const { groups, loading: usersLoading } = useSelector(
  (state: RootState) => state.users,
 );
 const { items: accessGroups, loading: accessGroupsLoading } = useSelector(
  (state: RootState) => state.accessGroups,
 );
 const isDisabled = !!accessGroupsLoading.getTable || !!usersLoading.updateItem;
 const { t } = useTranslation();

 const userGroupIds = item?.UserGroups?.map((g: GroupDTO) => g.Id) || [];
 const userAccessGroupIds =
  item?.AccessGroups?.map((g: AccessGroupsDTO) => g.Id) || [];

 useEffect(() => {
  const data = { limit: 1000 };
  dispatch(usersGetUserGroupsStart({ ...data }));
  dispatch(
   accessGroupsSlice.actions.getTableStart({
    ...data,
    table: 'accessGroups',
   }),
  );
 }, [dispatch]);

 const handleGroupToggle = (groupId: number, isChecked: boolean) => {
  let updatedGroupIds: number[];
  if (isChecked) {
   updatedGroupIds = [...userGroupIds, groupId];
  } else {
   updatedGroupIds = userGroupIds.filter((id: number) => id !== groupId);
  }
  dispatch(
   updateItemStart({
    table: 'users',
    id: item.Id || '',
    data: {
     ...item,
     UserGroups: updatedGroupIds.map((id: number) => ({ Id: id })),
    },
    isPut: true,
   }),
  );
 };

 const handleAccessGroupToggle = (
  accessGroupId: string,
  isChecked: boolean,
 ) => {
  let updatedAccessGroupIds: (string | null | undefined)[];
  if (isChecked) {
   updatedAccessGroupIds = [...userAccessGroupIds, accessGroupId];
  } else {
   updatedAccessGroupIds = userAccessGroupIds.filter(
    (id) => id !== accessGroupId,
   );
  }
  dispatch(
   updateItemStart({
    table: 'users',
    id: item.Id || '',
    data: {
     ...item,
     AccessGroups: updatedAccessGroupIds.map((id) => ({ Id: id || '' })),
    },
    isPut: true,
   }),
  );
 };

 const handleLockoutToggle = (checked: boolean) => {
  const lockoutEndDate = checked
   ? new Date(
      new Date().setFullYear(new Date().getFullYear() + 100),
     ).toISOString()
   : null;
  dispatch(
   updateItemStart({
    table: 'users',
    id: item.Id || '',
    data: {
     ...item,
     LockoutEnabled: checked,
     LockoutEndDate: checked ? lockoutEndDate : null,
    },
    isPut: true,
   }),
  );
 };

 const handleTwoFAToggle = (checked: boolean) => {
  dispatch(
   updateItemStart({
    table: 'users',
    id: item.Id || '',
    data: { ...item, TwoFAEnabled: checked },
    isPut: true,
   }),
  );
 };

 const generalTab = (
  <Box sx={{ position: 'relative', p: 0 }}>
   <InfoList data={item} customFields={fields} apiRef={apiRef} />
   <ResetUserPassword email={item?.Email || ''} />

   <GenerateAPIKey user={item} />
   <Box sx={{ mt: '24px' }}>
    <UserSettingsSwitches
     lockoutEnabled={!!item?.LockoutEndDate}
     twoFAEnabled={!!item?.TwoFAEnabled}
     onLockoutToggle={handleLockoutToggle}
     onTwoFAToggle={handleTwoFAToggle}
     disabled={isDisabled}
    />
   </Box>
  </Box>
 );

 const additionalTab = (
  <Box sx={{ p: 0 }}>
   <DatabaseList user={item} apiRef={apiRef} />
   <UserAccessGroupsSection
    groups={groups}
    accessGroups={accessGroups}
    selectedGroupIds={userGroupIds}
    selectedAccessGroupIds={userAccessGroupIds.filter(
     (id): id is string => typeof id === 'string',
    )}
    onGroupToggle={handleGroupToggle}
    onAccessGroupToggle={handleAccessGroupToggle}
    loadingGroups={!!usersLoading.usersGetUserGroups}
    loadingAccessGroups={!!accessGroupsLoading.getTable}
    disabled={isDisabled}
   />
  </Box>
 );

 const tabs = [
  { id: 'general', label: t('user.sidebar.tabs.main'), content: generalTab },
  {
   id: 'additional',
   label: t('user.sidebar.tabs.additional'),
   content: additionalTab,
  },
 ];

 return (
  <DatabaseCheck item={item} apiRef={apiRef}>
   <Box sx={{ p: 0 }}>
    <XTabs tabs={tabs} ariaLabel={t('user.sidebar.tabsAriaLabel')} />
   </Box>
  </DatabaseCheck>
 );
};

export default UsersSidebar;
